import { Injectable, Inject } from '@angular/core';
import { get } from 'lodash';
import { isToday } from 'date-fns';

import { OptionOrder, OrderProductReference } from './_interfaces_/option-order-list.interface';
import { OrderDetailsMapped, OrderPriceMapped, OrderContactDetailsMapped } from './_interfaces_/option-order-details.mapped.interface';
import { OptionOrderDetails, OrderDisplayPrice, OrderContactDetails, OrderLineDetail } from './_interfaces_/option-order-details.interface';
import { SelectedPersonMapperService } from '../selected-person/selected-person-mapper.service';
import { OrderMapped } from '../order/_interfaces_/order.mapped.interface';
import { OptionOrderContactDetailsEnum, OptionOrderCostEnum, OptionOrderProductTypeEnum } from './option-order.enum';

@Injectable({
  providedIn: 'root',
})
export class OptionOrderMapperService {
  constructor(@Inject(SelectedPersonMapperService) private _selectedPersonMapperService: SelectedPersonMapperService) {}

  mapOptionOrders(orders: OptionOrder[]): OrderMapped[] {
    const hasOrders = orders?.length > 0;

    return hasOrders
      ? orders.map((order) => ({
          id: order.identifier,
          departureDate: order.departureDate,
          destination: order.destination,
          mainPassengerName: order.mainPassengerName,
          expiryDate: order.optionExpiryDate,
          expiryDateWarning: isToday(new Date(order.optionExpiryDate)),
          orderNumber: order.orderNumber,
          orderStatus: order.orderStatus,
          pnr: this.getPnrFromProductReferences(order.productReferences),
        }))
      : [];
  }

  getPnrFromProductReferences(references: OrderProductReference[]): string | null {
    if (references.length === 0) {
      return null;
    }

    const flightProduct = references.find((ref) => ref.productType === OptionOrderProductTypeEnum.flights);
    return flightProduct ? flightProduct.reference : null;
  }

  mapOrderDetails(order: OptionOrderDetails): OrderDetailsMapped {
    try {
      return {
        orderNumber: order.orderNumber,
        departureDate: order.departureDate,
        orderStatus: order.orderStatus,
        mainBookerName: order.mainBookerName,
        endDestination: order.endDestination,
        optionExpiryDate: order.optionExpiryDate,
        displayPrice: this.mapOrderDetailsPrice(order.displayPrice),
        contactDetails: this.mapOrderDetailsContactDetails(order.contactDetails),
        flightId: this.getFlightId(order.orderLineDetail),
        bookedFlight: order.bookedFlightDetails,
        flightChangeDetails: order.flightChangeDetails
      };
    } catch (error) {
      console.error(error);
    }
  }

  mapOrderDetailsPrice(price: OrderDisplayPrice): OrderPriceMapped {
    return {
      totalCost: get(price, OptionOrderCostEnum.totalCost, null),
      bookingCost: get(price, OptionOrderCostEnum.bookingCost, null),
    };
  }

  mapOrderDetailsContactDetails(contactDetails: OrderContactDetails): OrderContactDetailsMapped {
    return {
      phoneNumber: get(contactDetails, OptionOrderContactDetailsEnum.phoneNumber, null),
      emailAddress: get(contactDetails, OptionOrderContactDetailsEnum.emailAddress, null),
      selectedPerson: this._selectedPersonMapperService.mapSelectedPerson(contactDetails.selectedPerson),
    };
  }

  getFlightId(lineDetails: OrderLineDetail[]): string {
    const url = lineDetails[0].linkHref;
    const splittedUrl = url.split('/');
    return splittedUrl[splittedUrl.length - 1];
  }
}
